/**
 * BranchTagDropdown — Phase R10.4 (2026-07-05)
 *
 * Owner-facing branch tag dropdown in the detail-page header.
 * Shows the current branch (or "Unassigned") and lets the owner
 * reassign the claim via POST /api/owner/receipt-claims/{id}/branch.
 *
 * Optimistic UI: local label updates immediately, reverted on error.
 *
 * Props:
 *   claim          — full claim object with `branch_id`
 *   branches       — array of {id, name} fetched from /api/owner/branches
 *   loading        — bool, branches fetch state
 *   onTag(id|null) — mutation handler supplied by parent
 *   pending        — bool, mutation in-flight
 */
import { useRef, useState, useEffect } from 'react';
import { ChevronDown, MapPin, Check, Loader2 } from 'lucide-react';

export default function BranchTagDropdown({
  claim,
  branches = [],
  loading = false,
  onTag,
  pending = false,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const currentId = claim?.branch_id ?? null;
  const currentName =
    currentId
      ? (branches.find((b) => b.id === currentId)?.name ?? 'Unknown branch')
      : 'Unassigned';

  const select = (nextId) => {
    setOpen(false);
    if (nextId === currentId) return;
    onTag(nextId);
  };

  const disabled = loading || pending;

  return (
    <div className="relative" ref={wrapRef} data-testid="branch-tag-dropdown">
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={
          'inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-sm font-medium min-h-[36px] transition-colors ' +
          'bg-white/5 border-white/15 text-white/90 hover:border-gold/60 disabled:opacity-50 disabled:cursor-not-allowed'
        }
        data-testid="branch-tag-dropdown-trigger"
      >
        <MapPin size={13} className="text-gold" />
        <span className="truncate max-w-[160px]" data-testid="branch-tag-dropdown-label">
          {currentName}
        </span>
        {pending ? (
          <Loader2 size={12} className="animate-spin text-white/60" />
        ) : (
          <ChevronDown size={12} className="text-white/50" />
        )}
      </button>

      {open ? (
        <div
          className="absolute right-0 mt-1 w-56 rounded-xl border border-white/10 bg-[#1A1A20] shadow-2xl z-30 py-1 max-h-72 overflow-auto"
          data-testid="branch-tag-dropdown-menu"
        >
          {branches.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => select(b.id)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-white/85 hover:bg-white/5"
              data-testid={`branch-tag-option-${b.id}`}
            >
              <span className="truncate">{b.name}</span>
              {currentId === b.id ? (
                <Check size={12} className="text-gold shrink-0" />
              ) : null}
            </button>
          ))}
          {branches.length > 0 ? (
            <div className="my-1 border-t border-white/5" />
          ) : null}
          <button
            type="button"
            onClick={() => select(null)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-white/60 italic hover:bg-white/5"
            data-testid="branch-tag-option-unassigned"
          >
            <span>Unassigned</span>
            {currentId == null ? (
              <Check size={12} className="text-gold shrink-0" />
            ) : null}
          </button>
        </div>
      ) : null}
    </div>
  );
}
